import dayjs from 'dayjs'

import { LineChart } from '@/pages/ui/line-chart'
import { LineChartActiveMarker } from '@/pages/ui/line-chart/line-chart-active-marker'
import { LineChartCurrentMarker } from '@/pages/ui/line-chart/line-chart-current-marker'
import { LineChartDataLine } from '@/pages/ui/line-chart/line-chart-data-line'
import { LineChartDropLine } from '@/pages/ui/line-chart/line-chart-drop-line'
import { LineChartTickLines } from '@/pages/ui/line-chart/line-chart-tick-lines'
import { LineChartTooltip } from '@/pages/ui/line-chart/line-chart-tooltip'
import { LineChartXAxis } from '@/pages/ui/line-chart/line-chart-x-axis'
import { LineChartYAxis } from '@/pages/ui/line-chart/line-chart-y-axis'

import { VisaBulletinTrendChartTooltip } from '../../../../../../../ui/chart-tooltips/visa-bulletin-trend-chart-tooltip/visa-bulletin-trend-chart-tooltip'
import type { VisaBulletinTrendMonthlySeriesItem } from '../../../../../../../ui/types'
import { VisaBulletinTrendChartMyPriorityDate } from '../../../../../../../ui/visa-bulletin-trend-chart-my-priority-date/visa-bulletin-trend-chart-my-priority-date'
import { PLOT_AREA_PADDING_RIGHT, X_AXIS_HEIGHT, Y_AXIS_WIDTH } from './constants'
import { PLOT_AREA_HEIGHT, PLOT_AREA_WIDTH } from './constants'
import { PLOT_AREA_PADDING_TOP } from './constants'
import { VisaBulletinTrendChartLegend } from './visa-bulletin-trend-chart-legend'

interface Props {
  monthlySeries: VisaBulletinTrendMonthlySeriesItem[]
  priorityDate?: string
}

export const VisaBulletinTrendChart: React.FC<Props> = ({ monthlySeries, priorityDate }) => {
  return (
    <>
      <LineChart
        data={monthlySeries}
        dataKeys={['filingDateDecimalYear', 'finalActionDateDecimalYear']}
        plotAreaWidth={PLOT_AREA_WIDTH}
        plotAreaHeight={PLOT_AREA_HEIGHT}
        plotAreaPadding={{
          right: PLOT_AREA_PADDING_RIGHT,
          top: PLOT_AREA_PADDING_TOP,
        }}
        xAxisHeight={X_AXIS_HEIGHT}
        yAxisWidth={Y_AXIS_WIDTH}
      >
        <LineChartXAxis dataKey="date" fontSize={10} tickFormatter={formatTick} />
        <LineChartYAxis title="Date" />
        <LineChartTickLines />
        <LineChartDataLine dataKey="filingDateDecimalYear" noGradient strokeWidth={2} />
        <LineChartDataLine
          dataKey="finalActionDateDecimalYear"
          noGradient
          strokeWidth={2}
          color="hsla(21, 90%, 48%, 1)"
        />
        <LineChartCurrentMarker
          dataKey="filingDateDecimalYear"
          render={(point) => (
            <>
              <circle cx={point.x} cy={point.y} r="5.5" fill="white" />
              <circle cx={point.x} cy={point.y} r="4.5" fill="hsla(228, 55%, 42%, 1)" />
            </>
          )}
        />

        <LineChartDropLine dataKeys={['filingDateDecimalYear', 'finalActionDateDecimalYear']} />

        <VisaBulletinTrendChartMyPriorityDate priorityDate={priorityDate} />

        <LineChartCurrentMarker
          dataKey="finalActionDateDecimalYear"
          render={(point) => (
            <>
              <circle cx={point.x} cy={point.y} r="5.0" fill="white" />
              <circle cx={point.x} cy={point.y} r="4.0" fill="hsla(21, 90%, 48%, 1)" />
            </>
          )}
        />
        <LineChartActiveMarker
          dataKey="filingDateDecimalYear"
          noDropLine
          render={() => (
            <>
              <circle r="5.5" fill="white" />
              <circle r="4.5" fill="hsla(228, 55%, 42%, 1)" />
            </>
          )}
        />
        <LineChartActiveMarker
          dataKey="finalActionDateDecimalYear"
          noDropLine
          render={() => (
            <>
              <circle r="5.5" fill="white" />
              <circle r="4.5" fill="hsla(21, 90%, 48%, 1)" />
            </>
          )}
        />
        <LineChartTooltip
          dataKeys={['filingDateDecimalYear', 'finalActionDateDecimalYear']}
          render={() => <VisaBulletinTrendChartTooltip />}
        />
      </LineChart>

      <VisaBulletinTrendChartLegend />
    </>
  )
}

/**
 * Produces the formatted labels for the x-axis for the given tick.
 */
const formatTick = (tick: string, index: number, length: number) => {
  const isFirst = index === 0
  const isLast = index === length - 1
  const isHalf = index === Math.floor(length / 2)
  const day = dayjs(tick)
  const labels = [day.format('MMM')]

  if (isFirst || isLast || isHalf) {
    labels.push(day.format('YYYY'))
  }

  return labels
}
